import { Component, TemplateRef, ViewChild } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { BillingCountry } from '../../shared/models/billing-country.model';
import { AccountUser, MfaSetup, NewEmailModel, PaymentType, Role, UpdatePasswordModel, UserCompanyDetails } from '../../shared/models/user.model';
import { BillingCountryService } from '../../shared/services/billing-country.service';
import { BrowserUtils } from '../../shared/services/browser-utils';
import { CancelError, ConfirmedActionsService } from '../../shared/services/confirmed-actions.service';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../shared/services/validation.service';
import {
  canManageInvoiceRecipients,
  formatInvoiceRecipientEmails,
  invoiceRecipientEmailsValidator,
  normalizeInvoiceRecipientEmails,
  registerInvoiceRecipientValidationMessages,
  splitInvoiceRecipientEmails,
} from '../invoice-recipients';

const COMPANY_DETAILS_CONFIRMATION_FIELDS: (keyof UserCompanyDetails)[] = [
  'companyName',
  'vatNumber',
  'countryCode',
  'addressCity',
  'addressZip',
  'addressStreet',
  'addressState',
];

@Component({
  standalone: false,
  selector: 'app-account-form',
  templateUrl: 'account-form.component.html',
  styleUrls: ['./account-form.component.scss'],
})
export class AccountFormComponent {
  @ViewChild('passwordModalTpl', { read: TemplateRef, static: false }) passwordModalTpl: any;
  @ViewChild('emailModalTpl', { read: TemplateRef, static: false }) emailModalTpl: any;
  @ViewChild('devicesModalTpl', { read: TemplateRef, static: false }) devicesModalTpl: any;
  @ViewChild('mfaSetupModalTpl', { read: TemplateRef, static: false }) mfaSetupModalTpl: any;
  @ViewChild('disableMfaModalTpl', { read: TemplateRef, static: false }) disableMfaModalTpl: any;
  @ViewChild('companyDetailsModalTpl', { read: TemplateRef, static: false }) companyDetailsModalTpl: any;

  passwordModal: DialogRef;
  emailModal: DialogRef;
  mfaModal: DialogRef;
  mfaDisableModal: DialogRef;
  companyDetailsModal: DialogRef;

  loading = true;
  model: AccountUser;
  companyDetails: UserCompanyDetails;
  countries: BillingCountry[] = [];
  backButtonShow = true;

  currentPasswordType = 'password';
  newPasswordType = 'password';
  passwordModel: UpdatePasswordModel = {
    currentPassword: '',
    newPassword: '',
    repeatNewPassword: '',
  };
  emailModel: NewEmailModel = {
    newEmail: '',
  };

  mfaLoading = false;
  mfaModel = { mfaCode: '' };
  mfaSetup: MfaSetup;
  mfaForm: FormGroup;

  form: FormGroup;
  passwordForm: FormGroup;
  emailForm: FormGroup;
  ipWhitelistForm: FormGroup;

  ipWhitelistAddressList: string[] = [];
  parentAccountDetails: AccountUser = null;
  invoiceEmails = '';
  invoiceRecipientsLoaded = false;
  controlsDisabledForSub = [
    'companyName',
    'websiteUrl',
    'vatNumber',
    'addressZip',
    'countryCode',
    'addressCity',
    'addressState',
    'addressStreet',
    'departmentName',
    'dataProtectionOfficerEmail',
    'legalRepresentativeEmail',
    'procurementEmail',
  ];

  getVatNumberRequired() {
    return this.form.get('vatNumber').hasValidator(Validators.required);
  }

  private submitSection: 'profile' | 'company' | 'platform' | 'legalContacts' | 'security' | null = null;
  private savedModelSnapshot: AccountUser = null;
  private pendingCompanyConfirm = false;
  private pendingLegalContactsConfirm = false;

  constructor(
    public notificationService: NotificationService,
    public formBuilder: FormBuilder,
    public service: UsersService,
    public billingCountryService: BillingCountryService,
    public validationService: ValidationService,
    public modal: ModalService,
    public router: Router,
    private route: ActivatedRoute
  ) {
    this.service.i().subscribe(user => {
      user.testTtl = user.testTtlMin;
      this.model = {
        id: user.id,
        role: user.role,
        email: user.email,
        paymentType: user.paymentType,
        username: user.username,
        fullName: user.fullName,
        companyName: user.companyName,
        departmentName: user.departmentName,
        phone: user.phone,
        websiteUrl: user.websiteUrl,
        vatNumber: user.vatNumber,
        vatPercentage: user.vatPercentage,
        emailForAlert: user.emailForAlert,
        emailForInvoice: null,
        dataProtectionOfficerEmail: user.dataProtectionOfficerEmail,
        legalRepresentativeEmail: user.legalRepresentativeEmail,
        procurementEmail: user.procurementEmail,
        countryName: user.countryName,
        countryCode: user.countryCode,
        addressCity: user.addressCity,
        addressZip: user.addressZip,
        addressStreet: user.addressStreet,
        addressState: user.addressState,
        testTtl: user.testTtl,
        minBalance: user.minBalance,
        testRetriesEnabled: user.testRetriesEnabled,
        password: null,
        mfaEnabled: user.mfaEnabled ? user.mfaEnabled : false,
        ipWhitelistAddressList: user.ipWhitelistAddressList,
        ipWhitelistEnabled: user.ipWhitelistEnabled,
      };
      this.setCompanyDetails();

      if (this.canEditInvoiceRecipients()) {
        this.loadInvoiceRecipients();
      }

      if (user.role === Role.SUB || user.role === Role.DEPUTY) {
        this.service.getParentAccountDetails().subscribe((parentAccount: AccountUser) => {
          if (parentAccount) {
            this.setParentAccountDetails(parentAccount);
          }
        });
      }
      if (this.model.ipWhitelistAddressList != null && this.model.ipWhitelistAddressList.length > 0) {
        this.model.ipWhitelistAddressList.split(',').forEach(address => {
          this.ipWhitelistAddressList.push(address);
        });
      }
      if (!this.isAccountPrepaid()) {
        this.form.removeControl('minBalance');
      }
      billingCountryService.all().subscribe({
        next: countries => {
          this.countries = [...countries].sort((a, b) => a.name.localeCompare(b.name));
          this.syncCountryNameFromCode();
          if (this.hasParentAccount()) {
            this.form.controls.companyName.patchValue(user.companyName);
            this.form.controls.testTtl.patchValue(user.testTtl);
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
      this.captureSavedSnapshot();
      this.route.queryParams.subscribe(params => {
        if (params.mfa && !this.model.mfaEnabled) {
          this.onClickMfaEnable();
        }
      });
      if (this.isSub()) {
        this.controlsDisabledForSub.forEach(controlName => {
          this.form.get(controlName).disable();
        });
      }
    });

    this.form = formBuilder.group({
      fullName: ['', Vld.compose([Vld.required, Vld.minLength(3), Vld.maxLength(255)])],
      companyName: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(255)])],
      departmentName: ['', Vld.compose([Vld.minLength(1), Vld.maxLength(255)])],
      username: ['', Vld.compose([Vld.required, Vld.username(true)])],
      websiteUrl: [''],
      countryCode: ['', Vld.required],
      phone: ['', Vld.compose([Vld.required, Vld.globalPhoneNumber()])],
      emailForAlert: ['', Vld.compose([Vld.email(true)])],
      invoiceEmails: ['', invoiceRecipientEmailsValidator()],
      dataProtectionOfficerEmail: ['', Vld.compose([Vld.required, Vld.email(true)])],
      legalRepresentativeEmail: ['', Vld.compose([Vld.email(true)])],
      procurementEmail: ['', Vld.compose([Vld.email(true)])],
      addressCity: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(100)])],
      addressStreet: ['', Vld.compose([Vld.required, Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(255)])],
      addressState: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(1), Vld.maxLength(100)])],
      addressZip: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
      vatNumber: ['', Vld.compose([Vld.notSpacesOnly(), Vld.minLength(3), Vld.maxLength(20)])],
      testRetriesEnabled: [''],
      testTtl: ['', UsersService.getValidatorForTestTtlMin()],
      minBalance: ['', Vld.compose([Vld.digits(), Vld.noSpace(), Vld.min(0)])],
      vatPercentage: [''],
    });
    registerInvoiceRecipientValidationMessages(validationService);
    this.ipWhitelistForm = formBuilder.group({
      ipWhitelistAddress: ['', Vld.compose([Vld.cidrAddress(true)])],
    });
    this.validationService.checkVatCountries(this.form);

    const comparePasswordValidator = () => {
      if (this.passwordForm) {
        const newPasswordFormControl = this.passwordForm.get('newPassword');
        const repeatPasswordFormControl = this.passwordForm.get('repeatNewPassword');
        if (newPasswordFormControl.value !== repeatPasswordFormControl.value) {
          return {
            comparePassword: true,
          };
        }
      }
      return null;
    };
    this.passwordForm = formBuilder.group({
      currentPassword: ['', Vld.compose([Vld.required])],
      newPassword: ['', Vld.compose([Vld.required, Vld.maxLength(50), Vld.password(true)]), [Vld.passwordBackend(this.service)]],
      repeatNewPassword: ['', Vld.compose([Vld.required, comparePasswordValidator])],
    });
    validationService.addFormatter('comparePassword', validationService.createTextFormatter('Passwords must match'));

    this.emailForm = formBuilder.group({
      newEmail: ['', Vld.compose([Vld.required, Vld.email(true)])],
    });
    this.mfaForm = formBuilder.group({
      mfaCode: ['', Vld.compose([Vld.required, Vld.digits(true), Vld.minLength(UsersService.TOTP_LENGTH), Vld.maxLength(UsersService.TOTP_LENGTH)])],
    });

    this.validationService = validationService;
  }

  private setCompanyDetails() {
    this.companyDetails = {
      companyName: this.model.companyName,
      websiteUrl: this.model.websiteUrl,
      vatNumber: this.model.vatNumber,
      countryName: this.model.countryName,
      countryCode: this.model.countryCode,
      addressCity: this.model.addressCity,
      addressZip: this.model.addressZip,
      addressStreet: this.model.addressStreet,
      addressState: this.model.addressState,
    };
  }

  private setParentAccountDetails(parentAccount: AccountUser): void {
    this.parentAccountDetails = parentAccount;
  }

  onCountryChange(countryCode: string): void {
    this.model.countryCode = countryCode;
    this.syncCountryNameFromCode();
  }

  private syncCountryNameFromCode(): void {
    const country = this.countries.find(item => item.code === this.model.countryCode);
    if (country) {
      this.model.countryName = country.name;
    } else if (this.countries.length > 0) {
      this.model.countryName = '';
    }
  }

  private loadInvoiceRecipients(): void {
    this.invoiceRecipientsLoaded = false;
    this.service.getSelfInvoiceRecipients().subscribe({
      next: response => {
        this.invoiceEmails = formatInvoiceRecipientEmails(response?.emails);
        this.form.get('invoiceEmails')?.patchValue(this.invoiceEmails);
        this.invoiceRecipientsLoaded = true;
      },
      error: error => {
        this.validationService.handleRequestError(error, 'Account');
      },
    });
  }

  onSubmit() {
    if (this.companyDetailsModal) {
      this.companyDetailsModal.close();
    }
    this.loading = true;
    this.model.ipWhitelistEnabled = this.model.ipWhitelistAddressList?.length > 0;

    this.service
      .selfUpdate(this.model)
      .pipe(
        switchMap(() => {
          if (!this.canEditInvoiceRecipients() || !this.invoiceRecipientsLoaded) {
            return of(null);
          }
          const emails = splitInvoiceRecipientEmails(this.invoiceEmails);
          return this.service.updateSelfInvoiceRecipients({ emails });
        }),
        switchMap(() => this.service.i())
      )
      .subscribe({
        next: () => {
          this.notificationService.success('Profile updated', 'Account');

          this.companyDetails = {
            companyName: this.model.companyName,
            websiteUrl: this.model.websiteUrl,
            vatNumber: this.model.vatNumber,
            countryName: this.model.countryName,
            countryCode: this.model.countryCode,
            addressCity: this.model.addressCity,
            addressZip: this.model.addressZip,
            addressStreet: this.model.addressStreet,
            addressState: this.model.addressState,
          };

          if (this.canEditInvoiceRecipients()) {
            this.invoiceEmails = normalizeInvoiceRecipientEmails(this.invoiceEmails);
            this.form.get('invoiceEmails')?.patchValue(this.invoiceEmails);
          }
          this.loading = false;
          this.captureSavedSnapshot();
        },
        error: e => {
          this.loading = false;
          this.notificationService.error((this.model.id ? 'Updating' : 'Creating') + ' an error', 'Account');
          this.validationService.handleRequestError(e, 'Account');
        },
      });
  }

  companyDetailsChanged(): boolean {
    if (!(this.model?.role === Role.MAIN || this.model?.role === Role.DEPUTY)) {
      return false;
    }

    const savedDetails = this.savedModelSnapshot ?? this.companyDetails;
    return COMPANY_DETAILS_CONFIRMATION_FIELDS.some(field => (savedDetails?.[field] ?? '') !== (this.model[field] ?? ''));
  }

  requiresCompanyDetailsConfirmation(): boolean {
    return this.companyDetailsChanged() || this.dataProtectionOfficerEmailChanged();
  }

  private dataProtectionOfficerEmailChanged(): boolean {
    if (!(this.model?.role === Role.MAIN || this.model?.role === Role.DEPUTY)) {
      return false;
    }
    const savedEmail = this.savedModelSnapshot?.dataProtectionOfficerEmail ?? '';
    const currentEmail = this.model?.dataProtectionOfficerEmail ?? '';
    return savedEmail !== currentEmail;
  }

  hasParentAccount() {
    return !this.model || this.model.role === Role.SUB || this.model.role == Role.DEPUTY;
  }

  isAdmin() {
    return !this.model || this.model.role === Role.ADMIN;
  }

  isDeputy() {
    return !this.model || this.model.role === Role.DEPUTY;
  }

  isMain() {
    return !this.model || this.model.role === Role.MAIN;
  }

  isSub() {
    return !this.model || this.model.role === Role.SUB;
  }

  canEditSharedAccountSettings(): boolean {
    return !this.hasParentAccount() || this.isDeputy();
  }

  canEditInvoiceRecipients(): boolean {
    return canManageInvoiceRecipients(this.model?.role);
  }

  onPasswordSubmit() {
    this.loading = true;
    const model = {
      currentPassword: this.passwordModel.currentPassword,
      newPassword: this.passwordModel.newPassword,
    };
    this.service.updatePassword(this.model.id, model).subscribe(
      () => {
        this.loading = false;
        this.notificationService.success('Password changed', 'Account');
        this.passwordModalClose();
      },
      e => {
        this.loading = false;
        if (e.status === 403) {
          this.notificationService.error({
            title: 'Account',
            message: 'Current password incorrect',
          });
        } else {
          this.notificationService.error({
            title: 'Account',
            message: 'Updating password an error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
        }
      }
    );
  }

  onEmailFormSubmit(): void {
    this.loading = true;
    this.service.updateEmail(this.emailModel.newEmail).subscribe(
      () => {
        this.notificationService.success('Email changed successfully. Please check your inbox to verify your new e-mail.', 'Account');
        this.emailModalClose();
        this.loading = false;
        this.service.logout(['/login'], ['/users']).then(url => {
          this.router.navigate(url);
        });
      },
      error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Account',
          message: 'Error in updating email.',
          serviceName: 'GATEWAY',
          requestMessage: error.statusText,
          requestCode: error.status,
        });
        this.emailModalClose();
      }
    );
  }

  isPasswordType(field) {
    field += 'Type';
    return this[field] === 'password';
  }

  toggleMFA(isChecked: boolean): void {
    if (!this.model.mfaEnabled) {
      this.onClickMfaEnable();
    } else {
      this.onClickMfaDisable();
    }
  }

  togglePasswordType(field) {
    field += 'Type';
    this[field] = this[field] === 'password' ? 'text' : 'password';
  }

  passwordModalOpen() {
    this.passwordModal = this.modal.alert().component(this.passwordModalTpl).open();
    this.passwordModal.result.then(() => {
      this.passwordModel.currentPassword = '';
      this.passwordModel.newPassword = '';
      this.passwordModel.repeatNewPassword = '';
    });
  }

  changeEmailModalOpen() {
    this.emailModal = this.modal.alert().component(this.emailModalTpl).open();
  }

  confirmCompanyDetailsChangeModalOpen() {
    this.companyDetailsModal = this.modal.alert().component(this.companyDetailsModalTpl).open();
  }

  confirmCompanyDetailsChangeForSubmit() {
    this.pendingCompanyConfirm = false;
    this.pendingLegalContactsConfirm = false;
    this.confirmCompanyDetailsChangeModalOpen();
  }

  onConfirmCompanyDetails() {
    if (this.pendingCompanyConfirm) {
      this.companyDetailsModal?.close();
      this.performSectionSubmit('company');
      this.pendingCompanyConfirm = false;
    } else if (this.pendingLegalContactsConfirm) {
      this.companyDetailsModal?.close();
      this.performSectionSubmit('legalContacts');
      this.pendingLegalContactsConfirm = false;
    } else {
      this.onSubmit();
    }
  }

  emailModalClose() {
    if (this.emailModal) {
      this.emailModal.close(true);
    }
  }

  passwordModalClose() {
    if (this.passwordModal) {
      this.passwordModal.close(true);
    }
  }

  shouldShowPlatformSettings(): boolean {
    if (this.model.role === Role.SUB && this.model.paymentType === PaymentType.POSTPAID) {
      return false;
    }
    return true;
  }

  applyProfileChanges() {
    if (!this.isProfileSectionValid()) {
      this.markControlsAsTouched(['fullName', 'username', 'phone']);
      return;
    }
    this.submitSection = 'profile';
    this.performSectionSubmit('profile');
  }

  applyCompanyChanges() {
    if (!this.isCompanySectionValid()) {
      this.markControlsAsTouched([
        'companyName',
        'websiteUrl',
        'countryCode',
        'addressCity',
        'addressZip',
        'addressStreet',
        'addressState',
        'vatNumber',
        'departmentName',
      ]);
      return;
    }
    if (this.companyDetailsChanged()) {
      this.pendingLegalContactsConfirm = false;
      this.pendingCompanyConfirm = true;
      this.confirmCompanyDetailsChangeModalOpen();
      return;
    }
    this.submitSection = 'company';
    this.performSectionSubmit('company');
  }

  applyPlatformChanges() {
    if (!this.isPlatformSectionValid()) {
      this.markControlsAsTouched(['invoiceEmails', 'testTtl', 'emailForAlert', 'minBalance']);
      return;
    }
    this.submitSection = 'platform';
    this.performSectionSubmit('platform');
  }

  applyLegalContactsChanges() {
    if (!this.isLegalContactsSectionValid()) {
      this.markControlsAsTouched(['dataProtectionOfficerEmail', 'legalRepresentativeEmail', 'procurementEmail']);
      return;
    }
    if (this.dataProtectionOfficerEmailChanged()) {
      this.pendingCompanyConfirm = false;
      this.pendingLegalContactsConfirm = true;
      this.confirmCompanyDetailsChangeModalOpen();
      return;
    }
    this.submitSection = 'legalContacts';
    this.performSectionSubmit('legalContacts');
  }

  private performSectionSubmit(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'security') {
    this.loading = true;
    const payload = this.buildSectionPayload(section);
    this.service
      .selfUpdate(payload)
      .pipe(
        switchMap(() => {
          if (section !== 'platform' || !this.canEditInvoiceRecipients() || !this.invoiceRecipientsLoaded) {
            return of(null);
          }
          const emails = splitInvoiceRecipientEmails(this.invoiceEmails);
          return this.service.updateSelfInvoiceRecipients({ emails });
        }),
        switchMap(() => this.service.i())
      )
      .subscribe({
        next: () => {
          this.notificationService.success('Profile updated', 'Account');
          if (section === 'company') {
            this.companyDetails = {
              companyName: this.model.companyName,
              websiteUrl: this.model.websiteUrl,
              vatNumber: this.model.vatNumber,
              countryName: this.model.countryName,
              countryCode: this.model.countryCode,
              addressCity: this.model.addressCity,
              addressZip: this.model.addressZip,
              addressStreet: this.model.addressStreet,
              addressState: this.model.addressState,
            };
          }
          if (section === 'platform' && this.canEditInvoiceRecipients() && this.invoiceRecipientsLoaded) {
            this.invoiceEmails = normalizeInvoiceRecipientEmails(this.invoiceEmails);
            this.form.get('invoiceEmails')?.patchValue(this.invoiceEmails);
          }
          this.afterSuccessfulUpdate(section);
        },
        error: e => {
          this.loading = false;
          this.notificationService.error('Updating an error', 'Account');
          this.validationService.handleRequestError(e, 'Account');
        },
      });
  }

  private afterSuccessfulUpdate(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'security') {
    const overrides = this.getSectionOverrides(section);
    this.savedModelSnapshot = Object.assign({}, this.savedModelSnapshot, overrides);
    this.loading = false;
    this.submitSection = null;
  }

  private captureSavedSnapshot() {
    try {
      this.savedModelSnapshot = JSON.parse(JSON.stringify(this.model));
    } catch (e) {
      this.savedModelSnapshot = Object.assign({}, this.model);
    }
  }

  private buildSectionPayload(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'security'): AccountUser {
    const base: AccountUser = this.savedModelSnapshot
      ? (JSON.parse(JSON.stringify(this.savedModelSnapshot)) as AccountUser)
      : (JSON.parse(JSON.stringify(this.model)) as AccountUser);
    const overrides = this.getSectionOverrides(section);
    return Object.assign({}, base, overrides);
  }

  private getSectionOverrides(section: 'profile' | 'company' | 'platform' | 'legalContacts' | 'security'): Partial<AccountUser> {
    const m = this.model;
    switch (section) {
      case 'profile':
        return {
          fullName: m.fullName,
          username: m.username,
          phone: m.phone,
        };
      case 'company':
        return {
          companyName: m.companyName,
          websiteUrl: m.websiteUrl,
          countryName: m.countryName,
          countryCode: m.countryCode,
          departmentName: m.departmentName,
          addressCity: m.addressCity,
          addressZip: m.addressZip,
          addressStreet: m.addressStreet,
          addressState: m.addressState,
          vatNumber: m.vatNumber,
          vatPercentage: m.vatPercentage,
        };
      case 'platform':
        return {
          testTtl: m.testTtl,
          emailForAlert: m.emailForAlert,
          minBalance: m.minBalance,
          testRetriesEnabled: m.testRetriesEnabled,
        };
      case 'legalContacts':
        return {
          dataProtectionOfficerEmail: m.dataProtectionOfficerEmail,
          legalRepresentativeEmail: m.legalRepresentativeEmail,
          procurementEmail: m.procurementEmail,
        };
      case 'security':
        return {
          ipWhitelistAddressList: m.ipWhitelistAddressList,
          ipWhitelistEnabled: m.ipWhitelistAddressList?.length > 0,
        };
    }
  }

  private areControlsValid(names: string[]): boolean {
    return names.every(n => {
      const c = this.form.get(n);
      return !c || c.disabled || c.valid;
    });
  }

  isProfileSectionValid(): boolean {
    return this.areControlsValid(['fullName', 'username', 'phone']);
  }

  isCompanySectionValid(): boolean {
    const names = [
      'companyName',
      'websiteUrl',
      'countryCode',
      'departmentName',
      'addressCity',
      'addressZip',
      'addressStreet',
      'addressState',
      'vatNumber',
      'vatPercentage',
    ];
    return this.areControlsValid(names.filter(n => !!this.form.get(n)));
  }

  isPlatformSectionValid(): boolean {
    const names = ['testTtl', 'emailForAlert', 'minBalance', 'testRetriesEnabled'];
    if (!this.canEditInvoiceRecipients() || this.invoiceRecipientsLoaded) {
      names.push('invoiceEmails');
    }
    return this.areControlsValid(names.filter(n => !!this.form.get(n)));
  }

  isLegalContactsSectionValid(): boolean {
    return this.areControlsValid(['dataProtectionOfficerEmail', 'legalRepresentativeEmail', 'procurementEmail']);
  }

  private markControlsAsTouched(names: string[]) {
    names.forEach(n => {
      const c = this.form.get(n);
      if (c) {
        c.markAsTouched();
        c.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  // Security apply: persist IP whitelist settings independently
  applySecurityChanges() {
    // No extra validation gating: applying saves the current list state
    this.submitSection = 'security';
    this.performSectionSubmit('security');
  }

  isAccountPrepaid(): boolean {
    if (this.model.paymentType === PaymentType.PREPAID) {
      return true;
    }
    return false;
  }

  onClickMfaEnable() {
    this.mfaModel.mfaCode = '';
    this.mfaForm.reset();
    this.mfaLoading = true;
    this.mfaSetup = null;
    this.service
      .mfaSetup()
      .pipe(
        catchError((e, caught) => {
          this.mfaLoading = false;
          if (!(e instanceof CancelError)) {
            this.notificationService.error({
              title: 'Account',
              message: 'Setup 2FA',
              serviceName: 'GATEWAY',
              requestMessage: e.statusText,
              requestCode: e.status,
            });
          }
          this.mfaModal.close(false);
          throw e;
        })
      )
      .subscribe(setup => {
        this.mfaSetup = setup;
        this.mfaLoading = false;
      });

    this.mfaModal = this.modal.alert().isBlocking(true).component(this.mfaSetupModalTpl).open();
  }

  mfaSetupOnSubmit() {
    ConfirmedActionsService.setCurrentAction('Enable MFA');
    this.mfaLoading = true;
    this.service
      .mfaEnable(this.mfaModel.mfaCode)
      .pipe(
        catchError(e => {
          this.mfaLoading = false;
          this.notificationService.error({
            title: 'Account',
            message: '2FA enabling an error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          throw e;
        })
      )
      .subscribe(() => {
        this.mfaLoading = false;
        this.model.mfaEnabled = true;
        this.notificationService.success('2FA enabled', 'Account');
      });
  }

  onMfaCodeChange(mfaCode: string) {
    if (this.mfaForm.controls.mfaCode.valid && mfaCode.length === UsersService.TOTP_LENGTH) {
      this.mfaSetupOnSubmit();
    }
  }

  onMfaCodeDisableChange(mfaCode: string) {
    if (this.mfaForm.controls.mfaCode.valid && mfaCode.length === UsersService.TOTP_LENGTH) {
      this.mfaDisableOnSubmit();
    }
  }

  mfaDisableOnSubmit() {
    this.mfaLoading = true;
    this.service
      .mfaDisable(this.mfaModel.mfaCode)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Account',
            message: '2FA disabling an error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          this.mfaLoading = false;
          throw e;
        })
      )
      .subscribe(() => {
        this.model.mfaEnabled = false;
        this.notificationService.success('2FA disabled', 'Account');
        this.mfaDisableModal.close();
      });
  }

  onClickMfaDisable() {
    this.mfaModel.mfaCode = '';
    this.mfaForm.reset();
    this.mfaDisableModal = this.modal.alert().isBlocking(true).component(this.disableMfaModalTpl).open();
  }

  copyToClipboard(text: string) {
    BrowserUtils.copyToClipboard(text);
  }

  onSubmitIpWhitelistAddressForm() {
    const ipWhitelistFormControl: FormControl = this.ipWhitelistForm.get('ipWhitelistAddress') as FormControl;
    const address = ipWhitelistFormControl.value;
    if (this.ipWhitelistAddressList?.length >= 15) {
      ipWhitelistFormControl.setErrors({ customError: 'You can only enter up to 15 addresses.' });
      return;
    } else {
      if (ipWhitelistFormControl.errors) {
        delete ipWhitelistFormControl.errors['customError'];
      }
    }

    if (address && this.ipWhitelistForm.valid) {
      if (this.ipWhitelistAddressList.indexOf(address) == -1) {
        this.ipWhitelistAddressList.push(address);
        this.updateIpWhitelistAddressList();
        ipWhitelistFormControl.patchValue('');
        ipWhitelistFormControl.reset();
      } else {
        ipWhitelistFormControl.setErrors({ customError: 'The address you entered is already added.' });
      }
    }
  }

  onRemoveIpWhitelistAddress(address) {
    const ipWhitelistFormControl: FormControl = this.ipWhitelistForm.get('ipWhitelistAddress') as FormControl;
    this.ipWhitelistAddressList = this.ipWhitelistAddressList.filter(_ => _ !== address);
    this.updateIpWhitelistAddressList();
    ipWhitelistFormControl.reset();
  }

  updateIpWhitelistAddressList() {
    const str = this.ipWhitelistAddressList.join(',');
    this.model.ipWhitelistAddressList = str;
  }

  showDevicesModal() {
    this.modal.alert().dialogClass('modal-dialog large-modal').component(this.devicesModalTpl).open();
  }

  logout(): void {
    this.service.logout(['/login'], ['/users']).then(url => {
      this.router.navigate(url).finally(() => {});
    });
  }
}
